import { AnimatePresence, useIsPresent, useReducedMotion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import { CardLayer, CardStackContainer } from "./styles.js";

const VISIBLE_DEPTH = 3;

// Resting pose of each card, from the top of the stack down.
const LAYER_POSES = [
  { rotate: 0, scale: 1 },
  { rotate: -7, scale: 1 },
  { rotate: -12, scale: 0.95 },
];

// Where a closed card goes, and where an undone card comes back from.
const TOSSED_POSE = { x: 180, y: -56, rotate: 16, opacity: 0 };

// Where cards sink to when they drop below the visible layers.
const BURIED_POSE = { x: 0, y: 0, rotate: -16, scale: 0.9, opacity: 0 };

const SETTLE_TRANSITION = { type: "spring", stiffness: 260, damping: 24 };

const TOSS_TRANSITION = {
  default: { duration: 0.38, ease: [0.4, 0, 0.9, 0.5] },
  opacity: { duration: 0.3, delay: 0.08, ease: "easeIn" },
};

const INSTANT_TRANSITION = { duration: 0 };

function buildVariants(reduceMotion) {
  const settle = reduceMotion ? INSTANT_TRANSITION : SETTLE_TRANSITION;
  const toss = reduceMotion ? INSTANT_TRANSITION : TOSS_TRANSITION;

  return {
    stacked: (depth) => ({ ...LAYER_POSES[depth], x: 0, y: 0, opacity: 1, transition: settle }),
    offstack: (depth) => (depth === 0 ? { ...TOSSED_POSE, transition: toss } : { ...BURIED_POSE, transition: settle }),
  };
}

function StackedCard({ depth, variants, children }) {
  const ref = useRef(null);
  const isPresent = useIsPresent();
  const isInteractive = isPresent && depth === 0;
  // A tossed card flies over the stack, a buried one sinks beneath it.
  const exitZIndex = depth === 0 ? VISIBLE_DEPTH + 1 : 0;

  useEffect(() => {
    ref.current.inert = !isInteractive;
  }, [isInteractive]);

  return (
    <CardLayer
      ref={ref}
      custom={depth}
      variants={variants}
      initial="offstack"
      animate="stacked"
      exit="offstack"
      aria-hidden={!isInteractive}
      style={{ zIndex: isPresent ? VISIBLE_DEPTH - depth : exitZIndex, pointerEvents: isInteractive ? "auto" : "none" }}
    >
      {children}
    </CardLayer>
  );
}

export default function CardStack({ cards, animateEntrance = false, renderCard, onDismiss }) {
  const reduceMotion = useReducedMotion();
  const variants = useMemo(() => buildVariants(reduceMotion), [reduceMotion]);

  // The stack unmounts once the last card is dismissed, so hold that card back
  // until it has been tossed off the stack.
  const [leavingCard, setLeavingCard] = useState(null);
  const pendingDismissRef = useRef(null);
  const visibleCards = cards.filter((card) => card !== leavingCard).slice(0, VISIBLE_DEPTH);

  // Still save the dismissal if the page navigates away mid-toss.
  useEffect(() => {
    return () => {
      if (pendingDismissRef.current) {
        pendingDismissRef.current();
      }
    };
  }, []);

  const dismiss = (card) => {
    if (cards.length === 1) {
      pendingDismissRef.current = () => onDismiss(card);
      setLeavingCard(card);
    } else {
      onDismiss(card);
    }
  };

  const handleExitComplete = () => {
    const commitDismiss = pendingDismissRef.current;

    if (commitDismiss) {
      pendingDismissRef.current = null;
      setLeavingCard(null);
      commitDismiss();
    }
  };

  return (
    <CardStackContainer>
      <AnimatePresence
        initial={animateEntrance}
        onExitComplete={handleExitComplete}
      >
        {visibleCards.map((card, depth) => (
          <StackedCard
            key={card}
            depth={depth}
            variants={variants}
          >
            {renderCard(card, () => dismiss(card))}
          </StackedCard>
        ))}
      </AnimatePresence>
    </CardStackContainer>
  );
}
